/**
 * Death screen (HEX-65). Shown on combat defeat in place of the bare
 * "Return to roster" close bar: a summary of the character who just fell — name,
 * discoveries, gold, where + how they fell, and a snapshot of their deck — before
 * the single Return-to-roster action.
 *
 * Pure presentation. The summary is built by the caller (EncounterFlow) from the
 * still-live character + the death tile + the slaying enemy, *before* the archive
 * write lands; the deck snapshot reads the character's `inventory` (retained at
 * death — the permadeck artefact, ADR-0015 §7 reframe). `onReturn` is the host's
 * archive-then-exit hook.
 *
 * DOM-overlay-above-Pixi, reusing the shared interaction-modal chrome (same as
 * ChestFlow) and the card-chip visual language (`buildCardChip`).
 */

import type { Hex } from '../../core/hex';
import type { Inventory } from '../types';
import { CardHoverPanel, buildCardChip } from './cardHoverPanel';

export interface DeathSummary {
  readonly name: string;
  readonly discoveries: number;
  readonly gold: number;
  /** HP at the moment of death (0 for a combat defeat). */
  readonly hp: number;
  /** Tile where they fell. */
  readonly hex: Hex;
  /** Slayer / enemy name; `null` when unrecorded. */
  readonly cause: string | null;
  /** The retained deck (loadout) — rendered as a snapshot. */
  readonly inventory: Inventory;
}

export class DeathScreen {
  private modal: HTMLDivElement | null = null;
  private readonly hoverPanel = new CardHoverPanel();

  constructor(private readonly container: HTMLElement) {}

  /** Mount the screen. `onReturn` fires once when the player dismisses it. */
  start(summary: DeathSummary, onReturn: () => void): void {
    if (this.modal) return;
    const root = document.createElement('div');
    root.className = 'hex6-interaction-modal hex6-death-screen';
    root.dataset.role = 'death-screen';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Defeat');

    const panel = document.createElement('div');
    panel.className = 'hex6-interaction-modal__panel';

    const title = document.createElement('h2');
    title.className = 'hex6-interaction-modal__title hex6-death-screen__title';
    title.textContent = `${summary.name} has fallen`;

    const body = document.createElement('div');
    body.className = 'hex6-interaction-modal__body';

    // Where + how they fell.
    const fell = document.createElement('div');
    fell.className = 'hex6-death-screen__fell';
    fell.dataset.role = 'death-fell';
    fell.textContent =
      summary.cause === null
        ? `Fell at (${summary.hex.q}, ${summary.hex.r}).`
        : `Slain by ${summary.cause} at (${summary.hex.q}, ${summary.hex.r}).`;

    // Final stats.
    const stats = document.createElement('dl');
    stats.className = 'hex6-death-screen__stats';
    stats.dataset.role = 'death-stats';
    appendStat(stats, 'Discoveries', String(summary.discoveries));
    appendStat(stats, 'Gold', String(summary.gold));
    appendStat(stats, 'HP at death', String(summary.hp));

    // Deck snapshot — the loadout, in placement order (ADR-0022: the only store).
    const deck = document.createElement('div');
    deck.className = 'hex6-death-screen__deck';
    deck.dataset.role = 'death-deck';
    const deckLabel = document.createElement('div');
    deckLabel.className = 'hex6-death-screen__deck-label';
    const cards = summary.inventory.loadout.placements.map((p) => p.card);
    deckLabel.textContent = cards.length === 0 ? 'Deck — empty' : `Deck — ${cards.length} cards`;
    deck.appendChild(deckLabel);
    if (cards.length > 0) {
      const chips = document.createElement('div');
      chips.className = 'hex6-death-screen__chips';
      for (const card of cards) chips.appendChild(buildCardChip(card, this.hoverPanel));
      deck.appendChild(chips);
    }

    body.append(fell, stats, deck);

    const actions = document.createElement('div');
    actions.className = 'hex6-interaction-modal__actions';
    const ret = document.createElement('button');
    ret.type = 'button';
    ret.dataset.action = 'death-return';
    ret.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    ret.textContent = 'Return to roster';
    ret.addEventListener('click', () => onReturn());
    actions.appendChild(ret);

    panel.append(title, body, actions);
    root.appendChild(panel);
    this.container.appendChild(root);
    this.modal = root;
  }

  destroy(): void {
    this.hoverPanel.destroy();
    this.modal?.remove();
    this.modal = null;
  }
}

function appendStat(dl: HTMLDListElement, label: string, value: string): void {
  const dt = document.createElement('dt');
  dt.textContent = label;
  const dd = document.createElement('dd');
  dd.textContent = value;
  dl.append(dt, dd);
}
